'use client'

import { Suspense, lazy, useEffect, useState } from 'react'
const Spline = lazy(() => import('@splinetool/react-spline'))

interface SplineSceneProps {
  scene: string
  className?: string
}

// Function to check WebGL support
function checkWebGLSupport(): boolean {
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
    
    if (!gl) {
      return false
    }
    
    // Additional check for context loss
    const extension = gl.getExtension('WEBGL_lose_context')
    if (extension) {
      // Test if context can be created without immediate loss
      const testCanvas = document.createElement('canvas')
      const testGl = testCanvas.getContext('webgl2') || testCanvas.getContext('webgl')
      if (!testGl || testGl.isContextLost()) {
        return false
      }
    }
    
    return true
  } catch (error) {
    console.warn('WebGL support check failed:', error)
    return false
  }
}

export function SplineScene({ scene, className }: SplineSceneProps) {
  const [webglSupported, setWebglSupported] = useState<boolean | null>(null)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    // Check WebGL support on component mount
    const isSupported = checkWebGLSupport()
    setWebglSupported(isSupported)
  }, [])

  // Error boundary for Spline component
  const handleSplineError = () => {
    setHasError(true)
  }

  // Minimal loading fallback - just empty div with same background
  const LoadingFallback = () => (
    <div className="w-full h-full bg-transparent"></div>
  )

  // WebGL not supported fallback - subtle and minimal
  const WebGLFallback = () => (
    <div className={`w-full h-full bg-transparent ${className}`}>
      {/* Completely invisible fallback - no visual indication */}
    </div>
  )

  // Error fallback - also minimal
  const ErrorFallback = () => (
    <div className={`w-full h-full bg-transparent ${className}`}>
      {/* Completely invisible fallback - no visual indication */}
    </div>
  )

  // Show minimal loading while checking WebGL support
  if (webglSupported === null) {
    return <LoadingFallback />
  }

  // Show fallback if WebGL is not supported
  if (!webglSupported) {
    return <WebGLFallback />
  }

  // Show error fallback if there was an error
  if (hasError) {
    return <ErrorFallback />
  }

  // Render Spline component with error handling
  return (
    <Suspense fallback={<LoadingFallback />}>
      <div className={className} onError={handleSplineError}>
        <Spline
          scene={scene}
          className="w-full h-full"
          onError={handleSplineError}
        />
      </div>
    </Suspense>
  )
}